import React, { useState, useEffect } from 'react';
import {
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'i18n/TranslationProvider';

const AddEditTrailerModal = ({ isOpen, onClose, onSave, trailer }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [formData, setFormData] = useState(trailer || {});
  useEffect(() => {
    if (isOpen) {
      setFormData({
        ...trailer,
        licensePlate:
          typeof trailer?.licensePlate === 'object'
            ? trailer.licensePlate.licensePlate
            : trailer?.licensePlate || '',
        trailerCapacity:
          trailer?.trailerCapacity ?? trailer?.capacity ?? '',
        cmrInvoiceUploaded: Boolean(
          trailer?.cmrInvoiceUploaded ?? trailer?.hasCmrInvoice ?? false,
        ),
      });
    }
  }, [isOpen, trailer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    const normalizedPlate = String(formData.licensePlate || '')
      .trim()
      .toUpperCase();
    const parsedCapacity = Number(formData.trailerCapacity);
    if (!normalizedPlate) {
      toast({
        title: t('License plate is required.'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!Number.isInteger(parsedCapacity) || parsedCapacity < 1 || parsedCapacity > 20) {
      toast({
        title: t('Trailer capacity is required.'),
        description: t('Enter a valid capacity between 1 and 20 cars.'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    onSave({
      ...formData,
      licensePlate: normalizedPlate,
      trailerCapacity: parsedCapacity,
      cmrInvoiceUploaded: Boolean(formData.cmrInvoiceUploaded),
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {trailer ? t('Edit trailer') : t('Add trailer')}
        </ModalHeader>

        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>{t('License plate')}</FormLabel>
              <Input
                name="licensePlate"
                value={formData.licensePlate || ''}
                onChange={handleChange}
                textTransform="uppercase"
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>{t('Capacity')}</FormLabel>
              <Input
                type="number"
                min={1}
                max={20}
                name="trailerCapacity"
                value={formData.trailerCapacity ?? ''}
                onChange={handleChange}
                placeholder={t('How many cars can this trailer carry?')}
              />
              <Text mt={1} fontSize="xs" color="gray.500">
                {t('Set maximum number of cars this trailer can carry (1-20).')}
              </Text>
            </FormControl>
            <FormControl>
              <Checkbox
                isChecked={Boolean(formData.cmrInvoiceUploaded)}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    cmrInvoiceUploaded: event.target.checked,
                  }))
                }
              >
                {t('CMR Document')} ({t('Uploaded')})
              </Checkbox>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={handleSave}>
            {t('Save')}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t('Cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddEditTrailerModal;
